import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { Colors, Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  AssetType,
  isZapPublishedAtomFamily_SUSPENDS,
  getAssetLink,
} from "@zapier/canvas-state";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import {
  DARK_COLOR,
  getContrastingTextColor,
} from "../Canvas/nodeTypes/AssetNodeSkeleton/utils";
import { ConditionalTooltip } from "../ConditionalTooltip";
import styles from "./AssetLink.module.css";

type Props = {
  assetType: AssetType;
  assetId: string;
  assetName: string | undefined;
  interfaceProjectId: string | undefined;
  isViewOnlyMode: boolean;
  color: string;
};

function SuspendedComponent({
  assetType,
  assetId,
  assetName,
  interfaceProjectId,
  isViewOnlyMode,
  color,
}: Props) {
  const zapId = assetType === "zap" ? assetId : undefined;
  const isZapPublished = useAtomValue(isZapPublishedAtomFamily_SUSPENDS(zapId));
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  const assetLink = getAssetLink({
    assetType,
    assetId,
    interfaceProjectId,
    isZapPublished,
  });

  if (!assetLink) {
    return null;
  }

  // Ensures the icon is visible since the icon color changes based on the node color
  const contrastingBackgroundColor =
    getContrastingTextColor(color!, true) === DARK_COLOR
      ? Colors.UiPrimaryWeakest
      : Colors.UiPrimaryStrongest;

  return (
    <ConditionalTooltip
      content={`Open ${assetName}`}
      isViewOnlyMode={isViewOnlyMode}
    >
      <div className={styles.root}>
        <Button
          size="small"
          iconPosition="start"
          onPress={async () => {
            emitEventOnAssetOpen({
              asset: assetType!,
              location: "node",
            });
            window.open(assetLink, "_blank");
          }}
          isDisabled={isViewOnlyMode}
          variant="ghost"
          style={
            {
              color: getContrastingTextColor(
                isViewOnlyMode ? Colors.GrayWarm3 : color!,
                true,
              ),
              "--hover-bg-color": contrastingBackgroundColor,
            } as React.CSSProperties
          }
        >
          <Icon name="arrowOffsite" size={16} />
        </Button>
      </div>
    </ConditionalTooltip>
  );
}

export function AssetLink({
  assetType,
  assetId,
  assetName,
  interfaceProjectId,
  isViewOnlyMode,
  color,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        assetType={assetType}
        assetId={assetId}
        assetName={assetName}
        interfaceProjectId={interfaceProjectId}
        isViewOnlyMode={isViewOnlyMode}
        color={color}
      />
    </Suspense>
  );
}
